/**
 * 指纹辅助函数
 * 移植自 xhshow-go/xhshow/fp_helpers.go
 */

import { createHash, randomBytes } from 'crypto';
import { GPU_VENDORS, SCREEN_RESOLUTIONS, CANVAS_HASH } from './data.js';
import type { ScreenConfig } from '../types/index.js';

/**
 * 加权随机选择
 */
export function weightedRandomChoice<T extends string | number>(
  options: T[],
  weights: number[]
): T {
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  let r = Math.random() * totalWeight;

  let idx = -1;
  let currentWeight = 0;
  for (let i = 0; i < weights.length; i++) {
    currentWeight += weights[i];
    if (r <= currentWeight) {
      idx = i;
      break;
    }
  }

  // 如果因为舍入误差未设置 idx，则回退到最后一项
  if (idx === -1) {
    idx = weights.length - 1;
  }

  return options[idx];
}

/**
 * 获取渲染器信息
 */
export function getRendererInfo(): { vendor: string; renderer: string } {
  const rendererStr = GPU_VENDORS[Math.floor(Math.random() * GPU_VENDORS.length)];
  const parts = rendererStr.split('|');
  return {
    vendor: parts[0],
    renderer: parts.length >= 2 ? parts[1] : '',
  };
}

/**
 * 获取屏幕配置
 */
export function getScreenConfig(): ScreenConfig {
  const resStr = weightedRandomChoice(SCREEN_RESOLUTIONS.resolutions, SCREEN_RESOLUTIONS.weights);
  const parts = resStr.split(';');
  const width = parseInt(parts[0], 10);
  const height = parseInt(parts[1], 10);

  let availWidth: number;
  let availHeight: number;

  // 随机选择 True/False
  if (Math.random() < 0.5) {
    // True 分支 - 减少宽度
    const deduction = weightedRandomChoice([0, 30, 60, 80], [0.1, 0.4, 0.3, 0.2]);
    availWidth = width - deduction;
    availHeight = height;
  } else {
    // False 分支 - 减少高度
    const deduction = weightedRandomChoice([30, 60, 80, 100], [0.2, 0.5, 0.2, 0.1]);
    availWidth = width;
    availHeight = height - deduction;
  }

  return {
    width,
    height,
    availWidth,
    availHeight,
  };
}

/**
 * 生成 Canvas 哈希
 */
export function generateCanvasHash(): string {
  return CANVAS_HASH;
}

/**
 * 生成 WebGL 哈希
 */
export function generateWebglHash(): string {
  const bytes = randomBytes(32);
  const hash = createHash('md5').update(bytes).digest('hex');
  return hash;
}
